/**
 * DataSourcesTab Component
 *
 * Manages datasources for a project (CRUD operations)
 */

import { useState } from 'react';
import { useDataSources, useDataSourceMutations } from '../../hooks/useDataSources';
import { DataSourceDialog } from './DataSourceDialog';
import type { DataSourceDefinition } from '@onecreator/shared';

interface DataSourcesTabProps {
  projectId: string;
}

export function DataSourcesTab({ projectId }: DataSourcesTabProps) {
  const { dataSources, loading, error, reload } = useDataSources(projectId);
  const {
    deleteDataSource,
    testDataSource,
    loading: mutationLoading,
    error: mutationError,
  } = useDataSourceMutations(projectId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDataSource, setEditingDataSource] = useState<DataSourceDefinition | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ status: 'ok' | 'error'; message: string } | null>(null);

  // Handle delete datasource
  const handleDelete = async (id: string) => {
    if (!confirm(`Are you sure you want to delete datasource "${id}"?`)) {
      return;
    }

    try {
      await deleteDataSource(id);
      reload();
    } catch (err: any) {
      console.error('Delete failed:', err);
      alert(err.message || 'Failed to delete datasource');
    }
  };

  // Handle test connection
  const handleTest = async (id: string) => {
    setTestingId(id);
    setTestResult(null);

    try {
      const result = await testDataSource(id, 'suggest', {});

      if (result.status === 'ok') {
        setTestResult({
          status: 'ok',
          message: `Connection successful! Latency: ${result.latency}ms`,
        });
      } else {
        setTestResult({
          status: 'error',
          message: result.error?.message || 'Connection test failed',
        });
      }
    } catch (err: any) {
      setTestResult({
        status: 'error',
        message: err.message || 'Connection test failed',
      });
    } finally {
      setTestingId(null);
    }
  };

  // Handle edit datasource
  const handleEdit = (dataSource: DataSourceDefinition) => {
    setEditingDataSource(dataSource);
    setDialogOpen(true);
  };

  // Handle add new datasource
  const handleAdd = () => {
    setEditingDataSource(null);
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = (saved: boolean) => {
    setDialogOpen(false);
    setEditingDataSource(null);
    if (saved) {
      reload();
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p className="mt-2 text-sm text-gray-600">Loading datasources...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-800">Error loading datasources: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Data Sources</h3>
          <p className="mt-1 text-sm text-gray-500">
            Configure external data sources for your project
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Data Source
        </button>
      </div>

      {/* Mutation Error */}
      {mutationError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-800">{mutationError}</p>
        </div>
      )}

      {/* Test Result */}
      {testResult && (
        <div className={`border rounded-md p-4 ${
          testResult.status === 'ok'
            ? 'bg-green-50 border-green-200'
            : 'bg-red-50 border-red-200'
        }`}>
          <p className={`text-sm ${
            testResult.status === 'ok' ? 'text-green-800' : 'text-red-800'
          }`}>
            {testResult.message}
          </p>
        </div>
      )}

      {/* DataSources Table */}
      {dataSources.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No data sources</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by adding a new data source to your project.
          </p>
          <div className="mt-6">
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Data Source
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {dataSources.map((dataSource) => (
              <li key={dataSource.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <h4 className="text-sm font-medium text-indigo-600 truncate">
                          {dataSource.id}
                        </h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {dataSource.adapter}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          {dataSource.config.baseUrl}
                        </span>
                        {dataSource.config.auth && dataSource.config.auth.type !== 'none' && (
                          <span className="flex items-center">
                            <svg className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            {dataSource.config.auth.type}
                          </span>
                        )}
                        {dataSource.enabledOperations && (
                          <span className="text-xs">
                            Operations: {dataSource.enabledOperations.join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleTest(dataSource.id)}
                        disabled={testingId === dataSource.id || mutationLoading}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {testingId === dataSource.id ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-700 mr-1.5"></div>
                            Testing...
                          </>
                        ) : (
                          <>
                            <svg className="-ml-0.5 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Test
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(dataSource)}
                        disabled={mutationLoading}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="-ml-0.5 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(dataSource.id)}
                        disabled={mutationLoading}
                        className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="-ml-0.5 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* DataSource Dialog */}
      <DataSourceDialog
        projectId={projectId}
        isOpen={dialogOpen}
        dataSource={editingDataSource}
        onClose={handleCloseDialog}
      />
    </div>
  );
}
